"use client";

import { Link } from "@/i18n/navigation";
import { ChevronDown, ChevronRight } from "lucide-react";
import { MouseEvent } from "react";
import TitleH1 from "./fonts/TitleH1";

type Breadcrumb = { label: string; href?: string };

const Header = ({
  title,
  subtitle,
  eyebrow,
  breadcrumb,
  compact = false,
  showScrollCue,
  containerClass = "gs--page-column",
  align = "center",
}: {
  title: React.ReactNode;
  /** Short lead paragraph rendered below the title. */
  subtitle?: string;
  /** Small uppercase label rendered above the title (e.g. section name). */
  eyebrow?: string;
  /** Trail rendered in a strip below the hero; the last item is the current page. */
  breadcrumb?: Breadcrumb[];
  /** Tighter band — used by auth pages where there's no scroll-worthy content below. */
  compact?: boolean;
  /** Animated chevron at the bottom that scrolls past the hero. Defaults to true unless compact. */
  showScrollCue?: boolean;
  /**
   * Class(es) for the inner column that holds title/subtitle and breadcrumbs.
   * Defaults to the site-wide BEM column ("gs--page-column"). Use
   * "gs--page-column gs--page-column--narrow" for content-led pages that
   * read on a narrower column (e.g. /partner, /podcast).
   */
  containerClass?: string;
  /** Alignment of the title/subtitle block and breadcrumb nav. Defaults to "center". */
  align?: "start" | "center";
}) => {
  const cueVisible = showScrollCue === true && !compact;
  const alignClasses =
    align === "center" ? "items-center text-center" : "items-start";

  const onCueClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const hero = e.currentTarget.closest(".gs--page-header");
    let target = hero?.nextElementSibling as HTMLElement | null;
    // Skip past the breadcrumb strip if it's the immediate sibling.
    if (target?.classList.contains("gs--page-header__crumbs-strip")) {
      target = target.nextElementSibling as HTMLElement | null;
    }
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollBy({
        top: window.innerHeight * 0.7,
        behavior: "smooth",
      });
    }
  };

  return (
    <>
      <div
        className={`gs--page-header flex flex-col justify-center ${compact ? "gs--page-header--compact" : ""}`}
      >
        <div
          className={`gs--page-header__content ${containerClass} flex flex-col pt-6 md:pt-7 ${alignClasses}`}
        >
          {eyebrow && (
            <p className="gs--page-header__eyebrow mb-3 text-xs font-semibold tracking-[0.2em] text-white/80 uppercase sm:text-sm">
              {eyebrow}
            </p>
          )}

          <TitleH1
            title={title}
            className="gs--page-header__title text-white"
          />

          {subtitle && (
            <p className="gs--page-header__subtitle mt-4 max-w-3xl text-sm text-white/90 sm:text-base md:text-lg">
              {subtitle}
            </p>
          )}
        </div>

        {cueVisible && (
          <a
            href="#"
            onClick={onCueClick}
            aria-label="Scroll to content"
            className="gs--page-header__cue mx-auto mt-8 inline-flex text-white/80 transition-colors hover:text-white motion-safe:animate-bounce"
          >
            <ChevronDown size={28} />
          </a>
        )}
      </div>

      {breadcrumb && breadcrumb.length > 0 && (
        <div className="gs--page-header__crumbs-strip">
          <nav
            aria-label="Breadcrumb"
            className={`gs--page-header__crumbs-strip__inner ${containerClass} flex flex-wrap ${alignClasses}`}
          >
            {breadcrumb.map((crumb, idx) => {
              const isLast = idx === breadcrumb.length - 1;
              return (
                <div
                  key={`${crumb.label}-${idx}`}
                  className="flex items-center gap-1.5"
                >
                  {crumb.href && !isLast ? (
                    <Link
                      href={crumb.href}
                      className="text-primary font-medium transition-opacity hover:opacity-80"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span
                      aria-current={isLast ? "page" : undefined}
                      className={isLast ? "text-ring" : ""}
                    >
                      {crumb.label}
                    </span>
                  )}
                  {!isLast && <ChevronRight size={12} className="opacity-50" />}
                </div>
              );
            })}
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;
